import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";
import { scrollToId } from "./Navbar";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_after-paint-pro/artifacts/7nwnuuci_After%20rotten.jpg";
const HERO_BEFORE_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_after-paint-pro/artifacts/5del8b7l_before%20rotten.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: imgY }}
        initial={{ scale: 1.15 }}
        animate={{ scale: 1.03 }}
        transition={{ duration: 2.6, ease: EASE }}
      >
        <img
          src={HERO_IMG}
          alt="Weatherboard cottage freshly painted grey and navy by After Paint"
          className="w-full h-full object-cover"
        />
        <motion.div
          data-testid="hero-before-wipe"
          className="absolute inset-0 bg-ash"
          initial={{ clipPath: "inset(0 0% 0 0)" }}
          animate={{ clipPath: "inset(0 100% 0 0)" }}
          transition={{ duration: 2.6, delay: 1.1, ease: EASE }}
        >
          <img
            src={HERO_BEFORE_IMG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          className="absolute inset-y-0 w-[2px] bg-ember z-10"
          initial={{ left: "0%", opacity: 0 }}
          animate={{ left: "100%", opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.6, delay: 1.1, ease: EASE }}
          aria-hidden="true"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-coal/90 via-coal/45 to-coal/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-coal/30" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pt-40 pb-10 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex items-center gap-4 mb-6 md:mb-8"
        >
          <span className="block h-px w-12 bg-ember" />
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-ember">
            Northern NSW — Painting
          </span>
        </motion.div>

        <h1 className="hero-title font-serif font-light tracking-tighter leading-[0.95] text-[12vw] sm:text-[11vw] lg:text-[8.5vw]">
          <MaskedLine delay={0.25}>Every home</MaskedLine>
          <MaskedLine delay={0.4}>has an</MaskedLine>
          <MaskedLine delay={0.55} className="text-ember italic">
            after.
          </MaskedLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-10">
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="max-w-md text-smoke text-base md:text-lg font-light leading-relaxed"
          >
            Paint now, pay on settlement. After Paint gets homes market-ready
            across Australia — 30 years on the tools, and a finish buyers
            appreciate.
            <span
              data-testid="hero-compliance"
              className="inline-block mt-4 text-[8px] uppercase tracking-[0.18em] font-medium text-bone/70 bg-bone/5 border border-bone/20 px-2.5 py-1"
            >
              Licensed painter — Lic. 205444C · ABN 12 474 073 126
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="flex flex-wrap items-center gap-4"
          >
            <motion.button
              data-testid="hero-quote-button"
              whileTap={{ scale: 0.96 }}
              onClick={() => scrollToId("#quote")}
              className="bg-ember text-coal text-xs uppercase tracking-[0.2em] font-semibold px-9 py-4 rounded-full hover:bg-ember-dark transition-colors duration-300"
            >
              Request a Free Quote
            </motion.button>
            <a
              data-testid="hero-call-button"
              href="tel:0410514499"
              className="border border-bone/25 text-bone text-xs uppercase tracking-[0.2em] font-medium px-9 py-4 rounded-full hover:border-ember hover:text-ember transition-colors duration-300"
            >
              Call 0410 514 499
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 lg:right-24 z-10 hidden md:flex flex-col items-center gap-3"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-smoke [writing-mode:vertical-lr]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} className="text-ember" strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
}
